import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TireItem } from '@1po/1po-bff-fe-spec/generated/tire/response/TireSearchResponse';
import { RootState } from 'app/AppStore';
import { LIFECYCLE_W1, LIFECYCLE_W4 } from 'domains/catalog/Catalog.types';
import { getLoadedPrices, getStocksMapV2 } from 'domains/references';
import { useFetchPrices, useFetchTireStocks } from 'domains/references/References.requests';
import { useFetchTireBrandCategories } from 'domains/tires/Tire.requests';
import { getTireSearchResult } from 'domains/tires/Tire.store';
import { isLoading } from 'utils';
import { isTireAvailable } from './tireReferencesAvailability';

interface UseTireReferencesDataStateResult {
  allTires: TireItem[];
  refsForStocks: string[];
  stocks: ReturnType<typeof getStocksMapV2>;
  prices: ReturnType<typeof getLoadedPrices>;
  brandCategoriesMap: ReturnType<typeof useFetchTireBrandCategories>;
  stockStatuses: Array<string | undefined>;
  hasPendingStockStatuses: boolean;
  hasUnresolvedStockEntries: boolean;
  somePriceMissing: boolean;
  someStockLoading: boolean;
  availableTires: TireItem[];
  priceForAvailable: ReturnType<typeof getLoadedPrices>;
}

export const useTireReferencesDataState = (
  catalogTires: ReturnType<typeof getTireSearchResult> | undefined,
): UseTireReferencesDataStateResult => {
  const allTires = useMemo(() => catalogTires?.data?.tires ?? [], [catalogTires]);

  const refsForStocks = useMemo(
    () =>
      allTires
        .filter((tire) => !(tire.lifeCycle === LIFECYCLE_W1 || tire.lifeCycle === LIFECYCLE_W4))
        .map((item) => item.partNumber),
    [allTires],
  );
  const refsForPrices = useMemo(
    () => allTires.filter((tire) => tire.lifeCycle !== LIFECYCLE_W4).map((item) => item.partNumber),
    [allTires],
  );

  const stocks = useSelector((state: RootState) => getStocksMapV2(state, refsForStocks));
  const prices = useSelector((state: RootState) => getLoadedPrices(state, refsForPrices));
  const brandCategoriesMap = useFetchTireBrandCategories();

  const stockStatuses = useMemo(() => [...new Set([...stocks.values()].map((s) => s?.searchStatus))], [stocks]);
  const hasPendingStockStatuses = refsForStocks.length > 0 && stockStatuses.length === 0;
  const hasUnresolvedStockEntries = useMemo(
    () => refsForStocks.some((ref) => stocks.get(ref)?.searchStatus === undefined),
    [refsForStocks, stocks],
  );
  const somePriceMissing = prices.some((p) => isLoading(p.prices));
  const someStockLoading = stockStatuses.some((sp) => isLoading(sp));

  const availableTires = useMemo(() => allTires.filter((tire) => isTireAvailable(tire, stocks)), [allTires, stocks]);
  const priceForAvailable = prices.filter((p) => availableTires.find((a) => a.partNumber === p.reference));

  useFetchTireStocks(refsForStocks);
  useFetchPrices(refsForPrices);

  return {
    allTires,
    refsForStocks,
    stocks,
    prices,
    brandCategoriesMap,
    stockStatuses,
    hasPendingStockStatuses,
    hasUnresolvedStockEntries,
    somePriceMissing,
    someStockLoading,
    availableTires,
    priceForAvailable,
  };
};
